/**
 * ONE answer to "would the site show an ad here?" -- for `MotiveComponent` (the site's own slots) and
 * for the editors' units (`studio/views/studio-ad.component.tsx`, `frame-picker/ads/ad-slot.component.tsx`).
 *
 * Round 13 (Z4, owner review 5 S4/P2/P9). The three inputs, in the order they resolve on a page:
 *
 *   1. the settings document has answered: `MotivesService.effectiveMotives` is set. Until then the
 *      answer is `null` ("pending"), and a slot keeps its reserved box so nothing shifts when the ad
 *      arrives (the round-12 rule; `AD_PLACEMENT_DESIGN.md` 4.2);
 *   2. this visitor may see ads at this position: the effective document (`free` for anonymous and free
 *      accounts, `paid` for a paid one -- every gate false there; and every gate false for an hour after
 *      the user closes an ad, `MOTIVES_DISABLED_DATE`, or when the ad library cannot load,
 *      `MotivesService.adsenseBlocked`) has the position's gate on;
 *   3. the network is the one our `<ins>` speaks to: `externalMotiveType === 'google'`.
 *
 * An admin account is NOT a paid account: the e2e admin persona (`PACKAGE_TYPE_FREE`) gets the free
 * document and sees the same ads as an anonymous visitor (probed on QA 2026-09-22, R13_ADS_NOTES.md).
 *
 * "Would show" is a promise about OUR side only. AdSense may still answer `data-ad-status="unfilled"`
 * for a slot we render; that is a fact stamped on the `<ins>` afterwards, not something this predicate
 * can know in advance.
 */
import { container } from '../../../context/module';
import { MotivesService } from '../../../services/motives.service';
import { MotiveConfig, MotiveLocationType, MotiveLocationTypes } from '../../../utils/request.utils';

/**
 * The gate the editors share (D6): `convertPageRight` -- true in the free document, false in the paid
 * one, and literally the rail position the Studio's in-editor rectangle and the picker's Files rail sit in.
 */
export const EDITOR_AD_GATE: MotiveLocationType = MotiveLocationTypes.convertPageRight;

/** The pure rule, on an effective document. `null` while the document is still pending. */
export function adsWouldShowFor(
    effective: MotiveConfig | null | undefined,
    location: MotiveLocationType = EDITOR_AD_GATE,
): boolean | null {
    if (!effective) return null;
    if (!(effective as any)[location]) return false;
    return effective.externalMotiveType === 'google';
}

/**
 * CLS lane (review 7, owner ruling 2026-09-27): "we must NOT collapse ad slots" -- CLS is a Core Web Vital Google ranks
 * on. `adsWouldShowFor` decides whether an AD is requested; this decides whether the SPACE is there, and it is the one
 * predicate every ad box's layout reads (`MotiveComponent`, the Studio units, the picker units).
 *
 *   - `null`  -- the settings document is pending: reserve the box (a filled slot never shifts the page).
 *   - `true`  -- the served document has this position on for this visitor: the box stays for the life of the mount,
 *                filled, unfilled (`data-ad-status`), silent (the script never answered), the ad library blocked, or
 *                an ad closed within the hour -- blank in all of those, never removed.
 *   - `false` -- the served document has the position off (a paid account, a position switched off) or the network is
 *                not ours: no box.
 *
 * Measured before (phone 390, review 7 SUITE-01 / R6-S / R4-JA): the converter's below-tool slot went at ~1.4 s with
 * the ad library blocked (0.083 CLS), the Studio's phone top row at ~1.9 s blocked (0.096) and at ~8 s when AdSense
 * never answered (the preview jumped 106 px).
 */
export function adSpaceFor(
    effective: MotiveConfig | null | undefined,
    location: MotiveLocationType = EDITOR_AD_GATE,
): boolean | null {
    if (!effective) return null;
    const reserved = (effective as MotiveConfig & { reservedSlots?: { [key: string]: boolean } }).reservedSlots;
    const on = reserved ? reserved[location] : (effective as any)[location];
    if (!on) return false;
    return effective.externalMotiveType === 'google';
}

/** Follow `adSpaceFor` as `subscribeAdsWouldShow` follows `adsWouldShowFor`. Returns the unsubscribe. */
export function subscribeAdSpace(
    cb: (space: boolean | null) => void,
    location: MotiveLocationType = EDITOR_AD_GATE,
): () => void {
    const svc = motivesService();
    if (!svc) { cb(null); return () => { /* nothing bound */ }; }
    const sub = svc.onEffectiveMotiveSettingsChange.subscribe({ next: (m: MotiveConfig) => cb(adSpaceFor(m, location)) });
    return () => sub.unsubscribe();
}

/**
 * The site's answer right now, for both editors. `false` while the settings document is still pending
 * (render nothing rather than a box that may have to go); use `subscribeAdsWouldShow` to follow it.
 */
export function adsWouldShow(location: MotiveLocationType = EDITOR_AD_GATE): boolean {
    return adsWouldShowFor(motivesService()?.effectiveMotives, location) === true;
}

/**
 * Follow the answer: `cb` runs with the current value (`null` = pending, then `true`/`false`) and again
 * whenever the effective document changes -- login resolving to a paid account, an ad closed, the ad
 * library found blocked. Returns the unsubscribe.
 */
export function subscribeAdsWouldShow(
    cb: (show: boolean | null) => void,
    location: MotiveLocationType = EDITOR_AD_GATE,
): () => void {
    const svc = motivesService();
    if (!svc) { cb(null); return () => { /* nothing bound */ }; }
    const sub = svc.onEffectiveMotiveSettingsChange.subscribe({ next: (m: MotiveConfig) => cb(adsWouldShowFor(m, location)) });
    return () => sub.unsubscribe();
}

function motivesService(): MotivesService | null {
    try {
        return container.get(MotivesService) as MotivesService;
    } catch {
        return null;
    }
}
